import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { ChatMap } from 'tripPlanner/components/ChatMap'
import type { ChatMapMarker, MapMarkerRect } from 'tripPlanner/components/ChatMap'
import { useMapStyle } from 'tripPlanner/components/ChatMap/MapStyleContext'
import type { ChatMapOnMapData } from 'tripPlanner/assistant/mapTypes'
import type { MapUIComponentData } from './messageTypes'
import type { TripItineraryData } from 'tripPlanner/api/types'
import ChatMapCardPlaceholder from './ChatMapCardPlaceholder'
import { CHAT_MAP_CARD_FIT_PADDING } from './chatMapConstants'
import { useChatMapLiveSlot } from './ChatMapLiveSlotContext'
import ChatLinkHoverPreview, { type ChatLinkHoverTarget } from './ChatLinkHoverPreview'
import type { LinkRect } from './PlaceHoverPreview/types'
import { HOVER_CLOSE_DELAY_MS } from './PlaceHoverPreview/constants'
import noop from 'lib/function/noop'

interface Props {
  data: MapUIComponentData
  /** Stable id for the live-map slot coordinator (message + part). */
  slotId: string
  onPlaceClick: (data: { googlePlaceId: string; placeName: string }) => void
  onPlaceSearchClick: (data: { searchQuery: string; placeName: string }) => void
  onEnlarge?: (data: ChatMapOnMapData) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
}

type HoveredPinState = {
  markerKey: string
  placeName: string
  googlePlaceId?: string
  placeSearch?: string
  rect: LinkRect
}

const MapWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(320)};
  border-radius: ${rem(12)};
  overflow: hidden;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};

  /* Mapbox measures its canvas from the container; keep it edge-to-edge in the card. */
  > * {
    position: absolute;
    inset: 0;
  }

  .mapboxgl-map {
    width: 100%;
    height: 100%;
  }
`

/** Synthetic marker key — stable across stream updates (not a Google Place ID). */
function getPinMarkerKey(index: number): string {
  return `map-pin-${index}`
}

function toLinkRect(rect: MapMarkerRect): LinkRect {
  return {
    top: rect.top,
    bottom: rect.bottom,
    left: rect.left,
    right: rect.right,
    width: rect.width,
    height: rect.height,
  }
}

function ChatMapCard({
  data,
  slotId,
  onPlaceClick,
  onPlaceSearchClick,
  onEnlarge,
  itineraryData,
  onSendMessage = noop,
}: Props) {
  const hasExplicitZoom = typeof data.zoom === 'number' && Number.isFinite(data.zoom)
  const { mapStyleMode, onMapStyleModeChange } = useMapStyle()
  const { containerRef, isLive } = useChatMapLiveSlot(slotId)
  const isMobileOrTablet = useIsMobileOrTablet()
  const isMobileViewport = useScreenSizeOnly('mobile')
  // Match chat-link hover: desktop two-pane only.
  const canShowPinHover = !isMobileOrTablet && !isMobileViewport

  const [hoveredPin, setHoveredPin] = useState<HoveredPinState | null>(null)
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearHoverTimeout = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current)
      hoverCloseTimeoutRef.current = null
    }
  }, [])

  useEffect(() => () => {
    clearHoverTimeout()
  }, [clearHoverTimeout])

  const markers = useMemo<Array<ChatMapMarker>>(
    () => data.pins.map((pin, index) => ({
      markerKey: getPinMarkerKey(index),
      lat: pin.lat,
      lng: pin.lng,
      label: pin.name,
      name: pin.name,
    })),
    [data.pins],
  )

  const pinNames = useMemo(
    () => data.pins.map(pin => pin.name).filter(Boolean),
    [data.pins],
  )

  // Rough first paint only — Mapbox owns the camera after load and fits pins.
  const initialViewState = useMemo(() => ({
    latitude: data.center.lat,
    longitude: data.center.lng,
    zoom: hasExplicitZoom ? data.zoom! : 12,
  }), [data.center.lat, data.center.lng, data.zoom, hasExplicitZoom])

  const findPinByMarkerKey = useCallback((markerKey: string) => {
    const pinIndex = data.pins.findIndex((_, index) => getPinMarkerKey(index) === markerKey)
    if (pinIndex < 0) return undefined
    return data.pins[pinIndex]
  }, [data.pins])

  const handleMarkerClick = useCallback((markerKey: string) => {
    clearHoverTimeout()
    setHoveredPin(null)

    const pin = findPinByMarkerKey(markerKey)
    if (!pin) return

    if (pin.googlePlaceId) {
      onPlaceClick({ googlePlaceId: pin.googlePlaceId, placeName: pin.name })
      return
    }

    const searchQuery = pin.placeSearch || pin.name
    if (searchQuery) {
      onPlaceSearchClick({ searchQuery, placeName: pin.name })
    }
  }, [clearHoverTimeout, findPinByMarkerKey, onPlaceClick, onPlaceSearchClick])

  const handleMarkerMouseEnter = useCallback((markerKey: string, markerRect: MapMarkerRect) => {
    if (!canShowPinHover) return
    const pin = findPinByMarkerKey(markerKey)
    if (!pin) return

    clearHoverTimeout()
    setHoveredPin({
      markerKey,
      placeName: pin.name,
      googlePlaceId: pin.googlePlaceId ?? undefined,
      placeSearch: pin.placeSearch,
      rect: toLinkRect(markerRect),
    })
  }, [canShowPinHover, clearHoverTimeout, findPinByMarkerKey])

  const handleMarkerMouseLeave = useCallback(() => {
    if (!canShowPinHover) return
    clearHoverTimeout()
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoveredPin(null)
      hoverCloseTimeoutRef.current = null
    }, HOVER_CLOSE_DELAY_MS)
  }, [canShowPinHover, clearHoverTimeout])

  const handlePreviewMouseEnter = useCallback(() => {
    clearHoverTimeout()
  }, [clearHoverTimeout])

  const handlePreviewClose = useCallback(() => {
    clearHoverTimeout()
    setHoveredPin(null)
  }, [clearHoverTimeout])

  const hoverTarget = useMemo<ChatLinkHoverTarget | undefined>(() => {
    if (!hoveredPin) return undefined
    if (hoveredPin.googlePlaceId) {
      return {
        type: 'place',
        placeId: hoveredPin.googlePlaceId,
        placeName: hoveredPin.placeName,
        rect: hoveredPin.rect,
      }
    }
    const searchQuery = (hoveredPin.placeSearch || hoveredPin.placeName).trim()
    if (!searchQuery) return undefined
    return {
      type: 'placeSearch',
      searchQuery,
      placeName: hoveredPin.placeName,
      rect: hoveredPin.rect,
    }
  }, [hoveredPin])

  const handlePreviewPlaceClick = useCallback((place: { googlePlaceId: string; placeName: string }) => {
    onPlaceClick(place)
    handlePreviewClose()
  }, [handlePreviewClose, onPlaceClick])

  const handleEnlarge = useCallback(() => {
    if (!onEnlarge || data.pins.length === 0) return
    onEnlarge({
      center: data.center,
      ...(hasExplicitZoom ? { zoom: data.zoom } : {}),
      pins: data.pins.map(pin => ({
        name: pin.name,
        lat: pin.lat,
        lng: pin.lng,
        ...(pin.placeSearch ? { placeSearch: pin.placeSearch } : {}),
        ...(pin.googlePlaceId ? { googlePlaceId: pin.googlePlaceId } : {}),
      })),
    })
  }, [data.center, data.pins, data.zoom, hasExplicitZoom, onEnlarge])

  const canEnlarge = Boolean(onEnlarge) && data.pins.length > 0

  return (
    <MapWrapper ref={containerRef}>
      {isLive ? (
        <ChatMap
          markers={markers}
          scrollZoom={false}
          cameraMode="uncontrolled"
          initialViewState={initialViewState}
          skipInitialFitBounds={false}
          fitBoundsDurationMs={0}
          fitBoundsPadding={CHAT_MAP_CARD_FIT_PADDING}
          showZoomControl={false}
          styleTogglePosition="bottom-right"
          mapStyleMode={mapStyleMode}
          onMapStyleModeChange={onMapStyleModeChange}
          onEnlarge={canEnlarge ? handleEnlarge : undefined}
          onMarkerClick={handleMarkerClick}
          onMarkerMouseEnter={canShowPinHover ? handleMarkerMouseEnter : undefined}
          onMarkerMouseLeave={canShowPinHover ? handleMarkerMouseLeave : undefined}
        />
      ) : (
        <ChatMapCardPlaceholder
          pinNames={pinNames}
          onEnlarge={canEnlarge ? handleEnlarge : undefined}
        />
      )}
      {canShowPinHover && hoverTarget && (
        <ChatLinkHoverPreview
          target={hoverTarget}
          isUser={false}
          isDevMode={false}
          onOfferClick={noop}
          onPlaceClick={handlePreviewPlaceClick}
          onClose={handlePreviewClose}
          onMouseEnter={handlePreviewMouseEnter}
          itineraryData={itineraryData}
          onSendMessage={onSendMessage}
        />
      )}
    </MapWrapper>
  )
}

export default ChatMapCard
